/*
 * Ring builder (Nivoda app) enhancements.
 * Loaded only on the ring-builder / diamond-feed page templates — see layout/theme.liquid.
 *
 * The app is a React bundle that renders asynchronously into #diamond-proxy-root
 * and re-renders freely, so everything here is (re)applied idempotently from a
 * MutationObserver. We rely only on Nivoda's "protected" class names.
 *
 * Features (numbers refer to the client's request list in data/BG Feed Changes.docx):
 *   #2  info (i) buttons on Colour / Clarity / Cut with educational pop-ups
 *   #3  "Our Recommendations" strip computed from the loaded results
 *   #6  single-option selects (ring size region) rendered as static text
 *   #8  collapsible filter cards with a current-value summary
 *   #10 fancy colour buttons tagged as swatches (painted by ring-builder.css)
 */
(function () {
	'use strict';

	var ROOT_ID = 'diamond-proxy-root';
	var STORAGE_KEY = 'rb-collapsed-filters';
	var BOOT_TIME = Date.now();

	/* Every card with a heading inside the app's filter container becomes
	   collapsible (#8) — discovered from the DOM rather than a fixed list so
	   the gemstones tab (different card class names) behaves the same as the
	   diamond tabs. On desktop cards start expanded; on mobile they start
	   collapsed and open one at a time, inline, full-width (see ring-builder.css). */
	var mobileMQ = window.matchMedia('(max-width: 749px)');

	var INFO_CONTENT = {
		'diamond-proxy-colour-filter': {
			title: 'Diamond Color',
			paragraphs: [
				'Color measures how close a white diamond is to being completely colorless. Grades run from D (no detectable color) down to M (a warm, noticeable tint).',
				'D–F diamonds are considered colorless and are the rarest. G–J are near-colorless — most people cannot tell them apart from colorless grades once set in a ring, which makes them excellent value.',
				'Fancy color diamonds (yellow, pink, blue and more) are graded on a separate scale of intensity, from Faint and Light up to Fancy Vivid and Fancy Deep. Stronger, more saturated color is rarer and more valuable.'
			],
			scale: [
				['D–F', 'Colorless'],
				['G–J', 'Near-colorless'],
				['K–M', 'Faint warmth']
			]
		},
		'diamond-proxy-clarity-filter': {
			title: 'Diamond Clarity',
			paragraphs: [
				'Clarity measures the tiny natural birthmarks — inclusions inside the stone and blemishes on its surface — left over from a diamond’s formation.',
				'Flawless (FL) and Internally Flawless (IF) stones are exceptionally rare. VVS and VS diamonds have inclusions that are very difficult to see even under 10× magnification.',
				'SI1–SI2 diamonds are usually "eye-clean" — inclusions visible under magnification but not to the naked eye — and are a popular value choice. I-grade diamonds have inclusions you may see unaided.'
			],
			scale: [
				['FL/IF', 'Flawless'],
				['VVS1–VVS2', 'Very, very slight'],
				['VS1–VS2', 'Very slight'],
				['SI1–SI2', 'Slight (often eye-clean)'],
				['I1–I3', 'Included']
			]
		},
		'diamond-proxy-cut-filter': {
			title: 'Diamond Cut',
			paragraphs: [
				'Cut describes how well a diamond’s proportions and facets interact with light — it is what gives a diamond its sparkle, and many experts consider it the most important of the four Cs.',
				'An Excellent cut reflects nearly all the light that enters the stone. Very Good cuts capture most of that brilliance at a lower price. Good and Fair cuts trade some fire and sparkle for size or budget.',
				'Because cut has the biggest impact on how impressive a diamond looks, we recommend prioritising Very Good or Excellent cuts before spending more on colour or clarity.'
			],
			scale: [
				['Excellent', 'Maximum brilliance'],
				['Very Good', 'Near-maximum, great value'],
				['Good', 'Solid sparkle'],
				['Fair', 'Budget option']
			]
		}
	};

	/* Swatch colours are painted in CSS; here we just tag the buttons. */
	var SWATCH_KEYS = {
		yellow: 'yellow', pink: 'pink', blue: 'blue', red: 'red', green: 'green',
		purple: 'purple', orange: 'orange', violet: 'violet', gray: 'gray',
		grey: 'gray', black: 'black', brown: 'brown', champagne: 'champagne',
		cognac: 'cognac', chameleon: 'chameleon', white: 'white', other: 'other',
		's & p': 'sp', 's&p': 'sp'
	};

	var COLOUR_ORDER = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];
	var CLARITY_ORDER = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'];
	var CUT_ORDER = ['ID', 'EX', 'VG', 'GD', 'G', 'F', 'FR', 'P'];

	/* ---------------------------------------------------------------- */
	/* Collapse state                                                    */
	/* ---------------------------------------------------------------- */

	/* Collapse state is kept per layout mode (mobile/desktop) so a session
	   that spans a resize keeps sensible defaults in both. */
	var collapseStates = {};

	function storageKey() {
		return STORAGE_KEY + (mobileMQ.matches ? '-m' : '-d');
	}

	function collapseState() {
		var key = storageKey();
		var state = collapseStates[key];
		if (!state) {
			try {
				state = JSON.parse(sessionStorage.getItem(key) || '{}') || {};
			} catch (e) { state = {}; }
			collapseStates[key] = state;
		}
		return state;
	}

	function saveCollapseState() {
		try {
			sessionStorage.setItem(storageKey(), JSON.stringify(collapseState()));
		} catch (e) { /* private mode etc. — non-fatal */ }
	}

	/* ---------------------------------------------------------------- */
	/* Apply loop                                                        */
	/* ---------------------------------------------------------------- */

	var scheduled = false;

	function schedule() {
		if (scheduled) return;
		scheduled = true;
		requestAnimationFrame(function () {
			scheduled = false;
			apply();
		});
	}

	function apply() {
		var root = document.getElementById(ROOT_ID);
		if (!root) return;
		if (root.dataset.primaryColor) {
			root.style.setProperty('--rb-primary', root.dataset.primaryColor);
		}
		/* One feature failing (e.g. the app's DOM shifts under us) must never
		   take the others — or the app itself — down with it. */
		[applyCollapse, applyRangeLimits, applyDefaultRange, applyDefaultGrades, applyDefaultPrice, applyShapeExpansion, applyInfoButtons, applySwatches, applySingleOptionSelects, applyRecommendations, applyAmericanSpelling, applyAssembledMessage].forEach(function (fn) {
			try {
				fn(root);
			} catch (e) {
				if (window.console && console.debug) console.debug('ring-builder.js:', fn.name, e);
			}
		});
	}

	/* ---------------------------------------------------------------- */
	/* #8 — collapsible filters with value summaries                     */
	/* ---------------------------------------------------------------- */

	/* Most filters have a direct h3; the colour filter nests its heading
	   inside a .color-block-header row (with the White/Fancy toggle).
	   Nivoda now also wraps some headings in their own plain div (e.g.
	   shape: <div><h3>Shape</h3></div>) — fall back to a plain descendant
	   search so we're not tied to the exact wrapper depth. Modals (the
	   only other h3-bearing markup ring-builder.js creates) are appended
	   to document.body, never inside a filter card, so this can't match
	   the wrong heading. */
	function getHeading(el) {
		return el.querySelector(':scope > h3, :scope > .color-block-header h3') || el.querySelector('h3');
	}

	/* The direct child of `el` that carries the heading — h3 itself,
	   .color-block-header, or (per the Nivoda DOM change noted above) some
	   other plain wrapper div. ring-builder.css needs to keep this element
	   visible when the card is collapsed, but it can't assume which of
	   those three shapes it is — so we tag it here and let CSS key off
	   the tag instead of the DOM structure. */
	function getHeadingRow(el, h3) {
		var row = h3;
		while (row.parentElement && row.parentElement !== el) row = row.parentElement;
		return row;
	}

	/* Stable state key for a filter card: its protected diamond-proxy class
	   where it has one, otherwise a slug of its heading text (gemstones). */
	function filterKey(el, h3) {
		for (var i = 0; i < el.classList.length; i++) {
			if (/^diamond-proxy-.+-filter$/.test(el.classList[i])) return el.classList[i];
		}
		var text = '';
		for (var n = h3.firstChild; n; n = n.nextSibling) {
			if (n.nodeType === 3) text += n.textContent;
		}
		return 'rb-' + text.trim().toLowerCase().replace(/\s+/g, '-');
	}

	function applyCollapse(root) {
		var state = collapseState();
		var containers = root.getElementsByClassName('diamond-proxy-filter-parent-container');
		for (var c = 0; c < containers.length; c++) {
			var kids = containers[c].children;
			for (var i = 0; i < kids.length; i++) {
				var el = kids[i];
				var h3 = getHeading(el);
				if (!h3) continue;
				var cls = filterKey(el, h3);
				if (typeof state[cls] !== 'boolean') state[cls] = mobileMQ.matches;

				var headingRow = getHeadingRow(el, h3);
				var priorRows = el.querySelectorAll(':scope > .rb-heading-row');
				for (var pr = 0; pr < priorRows.length; pr++) {
					if (priorRows[pr] !== headingRow) priorRows[pr].classList.remove('rb-heading-row');
				}
				if (!headingRow.classList.contains('rb-heading-row')) headingRow.classList.add('rb-heading-row');

				if (!el.classList.contains('rb-collapsible')) el.classList.add('rb-collapsible');
				var wantCollapsed = !!state[cls];
				if (el.classList.contains('rb-collapsed') !== wantCollapsed) {
					el.classList.toggle('rb-collapsed', wantCollapsed);
				}

				if (!h3.dataset.rbBound) {
					h3.dataset.rbBound = '1';
					h3.addEventListener('click', makeToggleHandler(cls));
				}

				/* Close (×) button for the mobile inline-expanded card (shown by CSS only there) */
				if (!el.querySelector(':scope > .rb-popup-close')) {
					var close = document.createElement('button');
					close.type = 'button';
					close.className = 'rb-popup-close';
					close.innerHTML = '&times;';
					close.setAttribute('aria-label', 'Close filters');
					close.addEventListener('click', collapseAllFilters);
					el.appendChild(close);
				}

				updateSummary(el, h3);
			}
		}
	}

	function makeToggleHandler(cls) {
		return function () {
			var state = collapseState();
			var expanding = state[cls];
			state[cls] = !state[cls];
			/* On mobile only one filter expands inline at a time, so the
			   page doesn't grow unmanageably tall with several open */
			if (expanding && mobileMQ.matches) {
				Object.keys(state).forEach(function (other) {
					if (other !== cls) state[other] = true;
				});
			}
			saveCollapseState();
			schedule();
		};
	}

	function collapseAllFilters() {
		var state = collapseState();
		Object.keys(state).forEach(function (cls) { state[cls] = true; });
		saveCollapseState();
		schedule();
	}

	/* Build a "G – D" style summary of the filter's current range from the
	   slider thumbs + scale labels, or from its min/max inputs. */
	function summariseFilter(el) {
		/* Client feedback: no range on the price card (class-matched on the
		   diamond tabs, heading-matched on the gemstones tab) */
		if (el.classList.contains('diamond-proxy-price-filter')) return '';
		var h3 = getHeading(el);
		if (h3 && /^price/i.test((h3.firstChild && h3.firstChild.textContent || '').trim())) return '';
		var thumbs = el.querySelectorAll('.thumb');
		if (thumbs.length) {
			/* The scale-label row is the element straight after the slider box */
			var track = el.querySelector('.track');
			var sliderBox = track ? track.parentElement : null;
			var labelsRow = sliderBox ? sliderBox.nextElementSibling : null;
			var labels = [];
			if (labelsRow) {
				for (var i = 0; i < labelsRow.children.length; i++) {
					labels.push((labelsRow.children[i].textContent || '').trim());
				}
			}
			var values = [];
			var valueMax = null;
			for (var t = 0; t < thumbs.length; t++) {
				var v = parseInt(thumbs[t].getAttribute('aria-valuenow'), 10);
				if (!isNaN(v)) values.push(v);
				var vm = parseInt(thumbs[t].getAttribute('aria-valuemax'), 10);
				if (!isNaN(vm)) valueMax = vm;
			}
			if (values.length && labels.length) {
				values.sort(function (a, b) { return a - b; });
				/* Boundary-style sliders (aria-valuemax === labels.length) place
				   thumbs on the N+1 boundaries around N grade labels, so the
				   upper thumb maps to the label *below* it. */
				var boundary = valueMax === labels.length;
				var loIdx = values[0];
				var hiIdx = boundary ? values[values.length - 1] - 1 : values[values.length - 1];
				if (hiIdx < loIdx) hiIdx = loIdx;
				var lo = labels[loIdx];
				var hi = labels[hiIdx];
				if (lo !== undefined && hi !== undefined) {
					return values.length > 1 && lo !== hi ? lo + ' – ' + hi : (hi || lo);
				}
			}
			/* No usable scale labels (e.g. price) — fall through to inputs */
		}
		var inputs = el.querySelectorAll('input');
		if (inputs.length >= 2) {
			var a = (inputs[0].value || '').trim();
			var b = (inputs[1].value || '').trim();
			if (a || b) return a + ' – ' + b;
		}
		return '';
	}

	function updateSummary(el, h3) {
		var text = summariseFilter(el);
		var span = h3.querySelector('.rb-summary');
		if (!text) {
			if (span) span.remove();
			return;
		}
		if (!span) {
			span = document.createElement('span');
			span.className = 'rb-summary';
			h3.appendChild(span);
		}
		if (span.textContent !== text) span.textContent = text;
	}

	/* ---------------------------------------------------------------- */
	/* Amend 2026-09 — the app's min/max block settings (see             */
	/* templates/page.ring-builder.json — carats_min/max, table_min/max, */
	/* ratio_min_v1/ratio_max) render as the *physical* min/max of the   */
	/* numeric filter inputs, shutting out anything outside the range    */
	/* the client configured. The client wants that range to be the      */
	/* default view only — values outside it should stay selectable.     */
	/* We widen the inputs back to each filter's native range and leave  */
	/* their current value alone, so the configured range is still what  */
	/* loads by default. Only confirmed against live DOM for Carat       */
	/* (rendered as a `.slider-inputs` pair of number inputs); Table and */
	/* Ratio are included on the same evidence (identical settings shape */
	/* — *_min/*_max on a continuous number filter) but unverified live. */
	/* ---------------------------------------------------------------- */
	/* defaultsKey looks up window.rbFilterDefaults[key + '_min'/'_max'] —
	   the "Nivoda Filters" theme settings, only wired up for carat so far. */
	var NATIVE_RANGES = [
		{ test: /^carat/i, min: 0, max: 10, defaultsKey: 'carats' },
		{ test: /^table/i, min: 0, max: 100 },
		{ test: /^ratio/i, min: 0, max: 3 }
	];

	function nativeRangeFor(heading) {
		for (var i = 0; i < NATIVE_RANGES.length; i++) {
			if (NATIVE_RANGES[i].test.test(heading)) return NATIVE_RANGES[i];
		}
		return null;
	}

	function applyRangeLimits(root) {
		var containers = root.getElementsByClassName('diamond-proxy-filter-parent-container');
		for (var c = 0; c < containers.length; c++) {
			var kids = containers[c].children;
			for (var i = 0; i < kids.length; i++) {
				var el = kids[i];
				var h3 = getHeading(el);
				if (!h3) continue;
				var range = nativeRangeFor((h3.firstChild && h3.firstChild.textContent || '').trim());
				if (!range) continue;
				var inputs = el.querySelectorAll('.slider-inputs input[type="number"]');
				for (var n = 0; n < inputs.length; n++) {
					var input = inputs[n];
					if (input.min !== String(range.min)) input.min = String(range.min);
					if (input.max !== String(range.max)) input.max = String(range.max);
				}
			}
		}
	}

	/* The filter inputs are React-controlled — a plain `input.value = x`
	   gets silently overwritten on the next render. Going through the
	   native setter before dispatching `input` mimics a real keystroke so
	   React's own onChange actually picks up the new value. */
	var nativeInputValueSetter = window.HTMLInputElement &&
		Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

	function setReactInputValue(input, value) {
		if (nativeInputValueSetter) {
			nativeInputValueSetter.call(input, value);
		} else {
			input.value = value;
		}
		input.dispatchEvent(new Event('input', { bubbles: true }));
		input.dispatchEvent(new Event('change', { bubbles: true }));
	}

	/* Applies the "Nivoda Filters" theme-setting defaults once per filter
	   card on load — a default view, not a hard limit (that's what
	   applyRangeLimits above is for). Only fires while the app's own
	   min/max settings (see templates/page.ring-builder.json) are left at
	   the native range, otherwise the default may sit outside what the
	   app currently allows and get silently rejected. */
	function applyDefaultRange(root) {
		var defaults = window.rbFilterDefaults;
		if (!defaults) return;
		var containers = root.getElementsByClassName('diamond-proxy-filter-parent-container');
		for (var c = 0; c < containers.length; c++) {
			var kids = containers[c].children;
			for (var i = 0; i < kids.length; i++) {
				var el = kids[i];
				if (el.dataset.rbDefaultApplied) continue;
				var h3 = getHeading(el);
				if (!h3) continue;
				var range = nativeRangeFor((h3.firstChild && h3.firstChild.textContent || '').trim());
				if (!range || !range.defaultsKey) continue;
				var min = defaults[range.defaultsKey + '_min'];
				var max = defaults[range.defaultsKey + '_max'];
				if (typeof min !== 'number' || typeof max !== 'number') continue;
				if (min > max || min < range.min || max > range.max) continue;
				var inputs = el.querySelectorAll('.slider-inputs input[type="number"]');
				if (inputs.length < 2) continue;
				el.dataset.rbDefaultApplied = '1';
				setReactInputValue(inputs[0], min);
				setReactInputValue(inputs[1], max);
			}
		}
	}

	/* ---------------------------------------------------------------- */
	/* Amend 2026-09 — client feedback (Stephen feedback.pdf): default    */
	/* selections for the grade-based filters (Clarity/Cut/Color), which  */
	/* render as a row of discrete "range-pill" buttons rather than the   */
	/* number-input pattern above (confirmed live for Cut only — see      */
	/* `.range-pill-active` / `aria-pressed` / `[data-horizontal-scroll]` */
	/* below). Clarity and Color are wired on the strength of the same    */
	/* naming and the PDF's own reference screenshot, not a live DOM      */
	/* check — harmless no-op if their markup differs, but verify live.   */
	/* Setting the default is two `button.click()`s (min grade, then max) */
	/* — plain buttons, so no React-controlled-input trick is needed.     */
	/* ---------------------------------------------------------------- */
	var GRADE_DEFAULTS = [
		{ test: /^cut/i, defaultsKey: 'cut', labels: { F: 'Fair', GD: 'Good', VG: 'Very Good', EX: 'Excellent', ID: 'Ideal', P: 'Poor' } },
		{ test: /^clarity/i, defaultsKey: 'clarity' },
		{ test: /^color/i, defaultsKey: 'color' }
	];

	function gradeLabel(config, code) {
		return (config.labels && config.labels[code]) || code;
	}

	/* A bare `button.click()` fires with clientX/Y at 0 — dispatch a real
	   MouseEvent positioned at the button's centre instead, in case the
	   component's range math reads click position rather than identity.
	   scrollIntoView first: a pill outside the horizontal-scroll row's
	   visible viewport (Clarity's 9 buttons vs. Cut's 4) would otherwise
	   report a real but off-screen rect, giving a clientX past what the
	   component's own visible-track math expects. */
	function firePillClick(button) {
		button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		var rect = button.getBoundingClientRect();
		button.dispatchEvent(new MouseEvent('click', {
			bubbles: true,
			cancelable: true,
			view: window,
			clientX: rect.left + rect.width / 2,
			clientY: rect.top + rect.height / 2
		}));
	}

	function findPill(container, label) {
		var buttons = container.querySelectorAll('button[aria-pressed]');
		for (var i = 0; i < buttons.length; i++) {
			if ((buttons[i].textContent || '').trim().toLowerCase() === label.toLowerCase()) return buttons[i];
		}
		return null;
	}

	/* A single shared queue + running flag — NOT one queue per
	   applyDefaultGrades() call. Filter cards become "ready" (their pill
	   buttons exist) at different moments across many apply() cycles, so
	   a naive per-call queue+recursion produced multiple *concurrent*
	   click chains that interleaved: Clarity's own min/max clicks got
	   clicks from Color's or Cut's chains landing in between, breaking
	   the click1→wait→click2 timing it depends on to form a range rather
	   than collapse to a single value. Newly-discovered filters are
	   appended here instead; if a chain is already running, they're
	   picked up by it rather than starting a second one. */
	var gradeQueue = [];
	var gradeQueueRunning = false;

	function runGradeQueue() {
		if (!gradeQueue.length) {
			gradeQueueRunning = false;
			return;
		}
		var pair = gradeQueue.shift();
		firePillClick(pair.min);
		setTimeout(function () {
			firePillClick(pair.max);
			setTimeout(runGradeQueue, 80);
		}, 80);
	}

	/* True once the Shape filter's force-expand/reorder dance (see
	   applyShapeExpansion below) has settled, or if there's no Shape
	   filter on this page at all. applyDefaultGrades waits on this — its
	   own click timing is tuned around an assumed-quiet window, and the
	   real clicks/re-renders Shape's takeover fires can land inside that
	   window and throw it off. */
	function isShapeSettled(root) {
		var cards = root.getElementsByClassName('diamond-proxy-shape-filter');
		if (!cards.length) return true;
		for (var i = 0; i < cards.length; i++) {
			if (!cards[i].dataset.rbShapeExpansionDone) return false;
		}
		return true;
	}

	/* Tracked in memory rather than as a dataset flag on the filter card's
	   own DOM node: if that node gets replaced by a React re-render (e.g.
	   from unrelated churn while Shape's takeover is still settling
	   nearby), a DOM-attribute flag is lost and the filter looks
	   unprocessed again — queuing a second, overlapping click sequence
	   that collapses the just-set range back to a single value. Keyed by
	   filter type, so it survives any number of remounts. */
	var gradeDefaultsQueued = {};

	function applyDefaultGrades(root) {
		var defaults = window.rbFilterDefaults;
		if (!defaults) return;
		/* Don't fight Shape's takeover for a quiet window — but don't wait
		   forever either, in case Shape's own process never settles. */
		if (!isShapeSettled(root) && (Date.now() - BOOT_TIME) < 2000) return;
		var containers = root.getElementsByClassName('diamond-proxy-filter-parent-container');
		for (var c = 0; c < containers.length; c++) {
			var kids = containers[c].children;
			for (var i = 0; i < kids.length; i++) {
				var el = kids[i];
				var h3 = getHeading(el);
				if (!h3) continue;
				var heading = (h3.firstChild && h3.firstChild.textContent || '').trim();
				var config = null;
				for (var g = 0; g < GRADE_DEFAULTS.length; g++) {
					if (GRADE_DEFAULTS[g].test.test(heading)) { config = GRADE_DEFAULTS[g]; break; }
				}
				if (!config || gradeDefaultsQueued[config.defaultsKey]) continue;
				var minCode = defaults[config.defaultsKey + '_min'];
				var maxCode = defaults[config.defaultsKey + '_max'];
				if (!minCode || !maxCode) continue;
				var pillContainer = el.querySelector('[data-horizontal-scroll="true"]');
				if (!pillContainer || !pillContainer.querySelector('button[aria-pressed]')) continue;
				var minPill = findPill(pillContainer, gradeLabel(config, minCode));
				var maxPill = findPill(pillContainer, gradeLabel(config, maxCode));
				if (!minPill || !maxPill) continue;
				gradeDefaultsQueued[config.defaultsKey] = true;
				gradeQueue.push({ min: minPill, max: maxPill });
			}
		}
		if (!gradeQueueRunning && gradeQueue.length) {
			gradeQueueRunning = true;
			runGradeQueue();
		}
	}

	/* ---------------------------------------------------------------- */
	/* Amend 2026-09 — client feedback: promote Radiant, Square Radiant   */
	/* and Marquise into the Shape filter's default (unexpanded) row, and */
	/* move Heart out of it. Confirmed live: the app only MOUNTS the      */
	/* first 7 `.filter-shape-item` nodes when collapsed — the other 28   */
	/* (all 35 shapes Nivoda supports; the `shapes` app-block setting has */
	/* no bearing on this) don't exist in the DOM at all until "More      */
	/* Shapes" is clicked. So reordering alone has nothing to work with   */
	/* on load.                                                           */
	/*                                                                    */
	/* Fix: force a one-time click on Nivoda's native toggle to mount all */
	/* 35, reorder them, then take over presentation entirely — hide      */
	/* Nivoda's own toggle, hide everything past our 9 promoted shapes,   */
	/* and drive our own "More/Less Shapes" button purely via a CSS       */
	/* class. We never click Nivoda's native toggle a second time:        */
	/* re-clicking its own handler would collapse back to its hardcoded   */
	/* first-7 set, unmounting our reordered items and undoing this.      */
	/* ---------------------------------------------------------------- */
	var SHAPE_PRIORITY = ['Round', 'Oval', 'Pear', 'Radiant', 'Marquise', 'Square', 'Emerald', 'Cushion', 'Trilliant', 'Heart', 'Octagonal', 'Other'];
	var SHAPE_FULL_COUNT_THRESHOLD = 20; /* well above those shown collapsed, comfortably below all ~35 */

	/* Reordering the same nodes into the same position is a harmless
	   no-op, so this runs unconditionally every cycle rather than once —
	   avoids a stale "already ordered" flag surviving a React re-render
	   that adds new (unordered) shape nodes to the same container. */
	function applyShapeOrder(root) {
		var items = root.querySelectorAll('.filter-shape-item');
		var parents = [];
		for (var i = 0; i < items.length; i++) {
			var parent = items[i].parentElement;
			if (parent && parents.indexOf(parent) === -1) parents.push(parent);
		}
		for (var p = 0; p < parents.length; p++) {
			var parent = parents[p];
			var kids = parent.querySelectorAll(':scope > .filter-shape-item');
			var byLabel = {};
			for (var k = 0; k < kids.length; k++) {
				var span = kids[k].querySelector('span');
				var label = ((span && span.textContent) || '').trim();
				byLabel[label] = kids[k];
			}
			/* Walk the priority list placing each item right after the
			   previously-placed one — NOT before a single fixed anchor
			   captured up front, which only ever pins the *first* item in
			   place while shunting everything else in front of it. */
			var cursor = null;
			for (var s = 0; s < SHAPE_PRIORITY.length; s++) {
				var item = byLabel[SHAPE_PRIORITY[s]];
				if (!item) continue;
				var refNode = cursor ? cursor.nextSibling : parent.firstChild;
				if (item !== refNode) parent.insertBefore(item, refNode);
				cursor = item;
			}
		}
	}

	/* Nivoda's "More Shapes"/"Fewer Shapes" toggle isn't a button — it's a
	   plain div with a chevron + text. Find the element whose own (not a
	   descendant's) text is the toggle label — using direct text-node
	   children here (the same technique filterKey() uses above) rather
	   than requiring zero children: the collapsed "More Shapes" state is
	   plain text with no icon, but the expanded "Fewer Shapes" state
	   pairs the label with a chevron icon element as a sibling, so a
	   childless-leaf requirement stops matching as soon as that icon is
	   present — which is exactly the state we need to find in order to
	   hide it. */
	function findShapeToggle(card) {
		/* Click/hide the element carrying the label text directly, not an
		   outer wrapper — click events only bubble UP to ancestors, so if
		   the real onClick handler lives on a more deeply nested node than
		   expected, clicking an ancestor would silently miss it. The node
		   whose own text node is the label is as deep as we can go while
		   still being guaranteed to contain it. */
		var all = card.querySelectorAll('div, span, button');
		for (var i = 0; i < all.length; i++) {
			var el = all[i];
			var text = '';
			for (var n = el.firstChild; n; n = n.nextSibling) {
				if (n.nodeType === 3) text += n.textContent;
			}
			text = text.trim();
			if (text === 'More Shapes' || text === 'Fewer Shapes') return el;
		}
		return null;
	}

	function applyShapeExpansion(root) {
		var cards = root.getElementsByClassName('diamond-proxy-shape-filter');
		for (var c = 0; c < cards.length; c++) {
			var card = cards[c];
			var items = card.querySelectorAll('.filter-shape-item');

			/* Nivoda's own responsive logic can silently reset this card
			   back to its native collapsed (7-item) default — e.g.
			   crossing the mobile breakpoint remounts the shape list from
			   scratch — without replacing the outer card element itself,
			   so our "done" flag survives on a card whose contents have
			   actually been reset underneath it. Detect that regression
			   and clear our flags (and any leftover custom toggle, whose
			   closure now points at nodes React has already discarded) so
			   the takeover below runs again from scratch. */
			if (card.dataset.rbShapeExpansionDone && items.length < SHAPE_FULL_COUNT_THRESHOLD) {
				delete card.dataset.rbShapeExpansionDone;
				delete card.dataset.rbShapeToggled;
				var staleToggle = card.querySelector(':scope > .rb-shape-toggle');
				if (staleToggle) staleToggle.remove();
			}

			if (card.dataset.rbShapeExpansionDone) continue;

			/* Hide the grid for the whole force-expand/reorder/re-collapse
			   dance below — briefly flashing all 35 shapes before settling
			   on 9 is worse than a short blank gap. Restored once the
			   takeover below finishes. */
			var loadingGrid = items.length ? items[0].parentElement : null;
			if (loadingGrid) loadingGrid.style.display = 'none';

			if (!card.dataset.rbShapeToggled) {
				if (items.length < SHAPE_FULL_COUNT_THRESHOLD) {
					var toggle = findShapeToggle(card);
					if (toggle) {
						card.dataset.rbShapeToggled = '1';
						toggle.click();
					}
					continue;
				}
				card.dataset.rbShapeToggled = '1';
			}

			if (items.length < SHAPE_FULL_COUNT_THRESHOLD) continue; /* waiting on React's re-render from the click above */

			applyShapeOrder(root);

			var nativeToggle = findShapeToggle(card);
			if (nativeToggle) {
				/* Hide the whole row (chevron included), not just the leaf
				   text node we clicked for reliable event bubbling. */
				var wrapper = nativeToggle;
				var label = (nativeToggle.textContent || '').trim();
				while (wrapper.parentElement && (wrapper.parentElement.textContent || '').trim() === label) {
					wrapper = wrapper.parentElement;
				}
				wrapper.style.display = 'none';
			}

			var grid = loadingGrid || items[0].parentElement;
			var kids = grid.querySelectorAll(':scope > .filter-shape-item');
			var hiddenItems = [];
			for (var k = SHAPE_PRIORITY.length; k < kids.length; k++) {
				kids[k].classList.add('rb-shape-hidden');
				hiddenItems.push(kids[k]);
			}

			var btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'rb-shape-toggle';
			btn.textContent = 'More Shapes';
			var expanded = false;
			btn.addEventListener('click', function () {
				expanded = !expanded;
				for (var h = 0; h < hiddenItems.length; h++) {
					hiddenItems[h].classList.toggle('rb-shape-hidden', !expanded);
				}
				btn.textContent = expanded ? 'Less Shapes' : 'More Shapes';
			});
			var closeBtn = card.querySelector(':scope > .rb-popup-close');
			card.insertBefore(btn, closeBtn || null);

			grid.style.display = '';
			card.dataset.rbShapeExpansionDone = '1';
		}
	}

	/* ---------------------------------------------------------------- */
	/* Amend 2026-09 — client feedback: Price should default to          */
	/* $1,500–$250,000 while the enforced range (see price_min/price_max */
	/* in templates/page.ring-builder.json) stays at the original wide   */
	/* bounds. Price's own inputs are a different shape again — plain    */
	/* `type="text"` fields (`inputmode="decimal"`) showing a formatted, */
	/* currency-converted value ("£1,125" for a $1,500 config value),    */
	/* not the `.slider-inputs input[type=number]` pattern Carat uses.   */
	/* Feeding the raw USD number through the same React-safe setter     */
	/* worked for Carat's number inputs and for Price's own *enforced*   */
	/* bounds (which the app itself formats/converts for display), so    */
	/* the same approach is used here — unverified live for Price        */
	/* specifically, in particular whether the paired log-scale thumb    */
	/* slider follows a plain-text input the same way it does a number   */
	/* input for Carat.                                                  */
	/* ---------------------------------------------------------------- */
	function applyDefaultPrice(root) {
		var defaults = window.rbFilterDefaults;
		if (!defaults) return;
		var min = defaults.price_min;
		var max = defaults.price_max;
		if (typeof min !== 'number' || typeof max !== 'number') return;
		var containers = root.getElementsByClassName('diamond-proxy-filter-parent-container');
		for (var c = 0; c < containers.length; c++) {
			var kids = containers[c].children;
			for (var i = 0; i < kids.length; i++) {
				var el = kids[i];
				if (el.dataset.rbPriceDefaultApplied) continue;
				var h3 = getHeading(el);
				if (!h3 || !/^price/i.test((h3.firstChild && h3.firstChild.textContent || '').trim())) continue;
				var inputs = el.querySelectorAll('input[inputmode="decimal"]');
				if (inputs.length < 2) continue;
				el.dataset.rbPriceDefaultApplied = '1';
				setReactInputValue(inputs[0], min);
				setReactInputValue(inputs[1], max);
			}
		}
	}

	/* ---------------------------------------------------------------- */
	/* #2 — info buttons + modal                                         */
	/* ---------------------------------------------------------------- */

	function applyInfoButtons(root) {
		Object.keys(INFO_CONTENT).forEach(function (cls) {
			var els = root.getElementsByClassName(cls);
			for (var i = 0; i < els.length; i++) {
				var h3 = getHeading(els[i]);
				if (!h3 || h3.querySelector('.rb-info-btn')) continue;
				var btn = document.createElement('button');
				btn.type = 'button';
				btn.className = 'rb-info-btn';
				btn.textContent = 'i';
				btn.setAttribute('aria-label', 'About ' + INFO_CONTENT[cls].title);
				btn.addEventListener('click', makeInfoHandler(cls));
				/* Insert straight after the heading text, before any summary */
				var summary = h3.querySelector('.rb-summary');
				h3.insertBefore(btn, summary || null);
			}
		});
	}

	function makeInfoHandler(cls) {
		return function (evt) {
			evt.stopPropagation(); /* don't toggle the collapse */
			evt.preventDefault();
			openInfoModal(INFO_CONTENT[cls]);
		};
	}

	var modalEls = null;

	function ensureModal() {
		if (modalEls) return modalEls;
		var backdrop = document.createElement('div');
		backdrop.className = 'rb-modal-backdrop';
		backdrop.hidden = true;

		var modal = document.createElement('div');
		modal.className = 'rb-modal';
		modal.setAttribute('role', 'dialog');
		modal.setAttribute('aria-modal', 'true');
		modal.hidden = true;

		var close = document.createElement('button');
		close.type = 'button';
		close.className = 'rb-modal-close';
		close.innerHTML = '&times;';
		close.setAttribute('aria-label', 'Close');

		var body = document.createElement('div');
		body.className = 'rb-modal-body';

		modal.appendChild(close);
		modal.appendChild(body);
		document.body.appendChild(backdrop);
		document.body.appendChild(modal);

		function hide() {
			backdrop.hidden = true;
			modal.hidden = true;
		}
		backdrop.addEventListener('click', hide);
		close.addEventListener('click', hide);
		document.addEventListener('keydown', function (e) {
			if (e.key === 'Escape') hide();
		});

		modalEls = { backdrop: backdrop, modal: modal, body: body };
		return modalEls;
	}

	function openInfoModal(content) {
		var els = ensureModal();
		var html = '<h4></h4>';
		els.body.innerHTML = html;
		els.body.querySelector('h4').textContent = content.title;
		content.paragraphs.forEach(function (text) {
			var p = document.createElement('p');
			p.textContent = text;
			els.body.appendChild(p);
		});
		if (content.scale) {
			var ul = document.createElement('ul');
			ul.className = 'rb-modal-scale';
			content.scale.forEach(function (pair) {
				var li = document.createElement('li');
				var strong = document.createElement('strong');
				strong.textContent = pair[0];
				li.appendChild(strong);
				li.appendChild(document.createTextNode(' ' + pair[1]));
				ul.appendChild(li);
			});
			els.body.appendChild(ul);
		}
		els.backdrop.hidden = false;
		els.modal.hidden = false;
	}

	/* ---------------------------------------------------------------- */
	/* #10 — tag fancy colour buttons for CSS swatches                   */
	/* ---------------------------------------------------------------- */

	function applySwatches(root) {
		var colourFilters = root.getElementsByClassName('diamond-proxy-colour-filter');
		for (var i = 0; i < colourFilters.length; i++) {
			var buttons = colourFilters[i].querySelectorAll('button');
			for (var b = 0; b < buttons.length; b++) {
				var btn = buttons[b];
				if (btn.dataset.rbSwatch || btn.classList.contains('rb-info-btn')) continue;
				/* Skip the White/Fancy mode toggle */
				if (btn.closest('.color-fancy-toggle-wrapper')) continue;
				var key = SWATCH_KEYS[(btn.textContent || '').trim().toLowerCase()];
				if (key) btn.dataset.rbSwatch = key;
			}
		}
	}

	/* ---------------------------------------------------------------- */
	/* Amend 2026-09 — American spelling: the app renders "Colour";      */
	/* the client wants "Color" everywhere.                              */
	/* ---------------------------------------------------------------- */

	function applyAmericanSpelling(root) {
		var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
			acceptNode: function (node) {
				return /[Cc]olour/.test(node.nodeValue)
					? NodeFilter.FILTER_ACCEPT
					: NodeFilter.FILTER_REJECT;
			}
		});
		var nodes = [];
		while (walker.nextNode()) nodes.push(walker.currentNode);
		nodes.forEach(function (node) {
			node.nodeValue = node.nodeValue.replace(/Colour/g, 'Color').replace(/colour/g, 'color');
		});
	}

	/* ---------------------------------------------------------------- */
	/* #6 — replace single-option selects with static text               */
	/* ---------------------------------------------------------------- */

	function applySingleOptionSelects(root) {
		var selects = root.querySelectorAll('select');
		for (var i = 0; i < selects.length; i++) {
			var sel = selects[i];
			if (sel.dataset.rbSingleOption) {
				/* Options can arrive late — undo if it grew past one */
				if (sel.options.length > 1) {
					delete sel.dataset.rbSingleOption;
					var stale = sel.previousElementSibling;
					if (stale && stale.classList.contains('rb-static-value')) stale.remove();
				}
				continue;
			}
			if (sel.options.length !== 1) continue;
			sel.dataset.rbSingleOption = '1';
			var span = document.createElement('span');
			span.className = 'rb-static-value';
			span.textContent = (sel.options[0].textContent || '').trim();
			sel.parentNode.insertBefore(span, sel);
		}
	}

	/* ---------------------------------------------------------------- */
	/* #3 — recommendations computed from the loaded result cards        */
	/* ---------------------------------------------------------------- */

	function parseCard(card) {
		var link = card.querySelector('a[href]');
		var titleEl = card.querySelector('.diamond-proxy-diamond-title');
		var img = card.querySelector('img');
		if (!link || !titleEl) return null;
		var title = (titleEl.textContent || '').trim();
		if (!title) return null;

		var caratMatch = title.match(/([\d.]+)\s*ct/i);
		var carat = caratMatch ? parseFloat(caratMatch[1]) : null;

		var specs = {};
		var subs = card.querySelectorAll('.view-details-subtitle');
		for (var i = 0; i < subs.length; i++) {
			var label = (subs[i].querySelector('.view-details-subtitle-label') || {}).textContent || '';
			var value = (subs[i].querySelector('.view-details-subtitle-value') || {}).textContent || '';
			label = label.replace(/[:\s]+$/, '').trim().toLowerCase();
			if (label) specs[label] = value.trim();
		}

		var price = null;
		var priceText = '';
		var details = card.querySelector('.diamond-proxy-product-details');
		if (details) {
			var walker = details.querySelectorAll('div, span, p');
			for (var w = 0; w < walker.length; w++) {
				var m = (walker[w].textContent || '').match(/^\s*([£$€])\s*([\d,]+(?:\.\d+)?)(\s+[A-Z]{3})?\s*$/);
				if (m) {
					price = parseFloat(m[2].replace(/,/g, ''));
					priceText = walker[w].textContent.trim();
					break;
				}
			}
		}

		if (price === null || carat === null) return null;

		return {
			href: link.getAttribute('href'),
			img: img ? img.getAttribute('src') : null,
			title: title,
			carat: carat,
			price: price,
			priceText: priceText,
			colour: specs.colour || specs.color || null,
			clarity: specs.clarity || null,
			cut: specs.cut || null
		};
	}

	function rank(order, value) {
		var idx = order.indexOf((value || '').toUpperCase());
		return idx === -1 ? order.length : idx;
	}

	function specScore(d) {
		return rank(COLOUR_ORDER, d.colour) + rank(CLARITY_ORDER, d.clarity) + rank(CUT_ORDER, d.cut);
	}

	function pickRecommendations(diamonds) {
		var picked = {};

		function best(sorter) {
			var pool = diamonds.filter(function (d) { return !picked[d.href]; });
			if (!pool.length) return null;
			pool.sort(sorter);
			picked[pool[0].href] = true;
			return pool[0];
		}

		var topSpecs = best(function (a, b) {
			return specScore(a) - specScore(b) || a.price - b.price;
		});
		var bestValue = best(function (a, b) {
			return (a.price / a.carat) - (b.price / b.carat) || a.price - b.price;
		});
		var largest = best(function (a, b) {
			return b.carat - a.carat || a.price - b.price;
		});

		var recs = [];
		if (topSpecs) recs.push({ badge: 'Top Specs', blurb: 'The cleanest grades across cut, color and clarity', d: topSpecs });
		if (bestValue) recs.push({ badge: 'Best Value', blurb: 'The most carat for your money', d: bestValue });
		if (largest) recs.push({ badge: 'Largest Carat', blurb: 'The biggest diamond in your results', d: largest });
		return recs;
	}

	function applyRecommendations(root) {
		var cards = root.getElementsByClassName('diamond-proxy-product-card');
		var existing = document.getElementById('rb-recs');

		var diamonds = [];
		for (var i = 0; i < cards.length; i++) {
			var d = parseCard(cards[i]);
			if (d) diamonds.push(d);
		}

		/* Too few results to make a meaningful recommendation */
		if (diamonds.length < 6) {
			if (existing) existing.remove();
			return;
		}

		/* Images can arrive after the cards do — include them in the signature
		   so the strip rebuilds once they load */
		var signature = diamonds.map(function (d) { return d.href + (d.img ? '#i' : ''); }).join('|');
		if (existing && existing.dataset.rbSignature === signature) return;

		var grid = cards[0].parentElement;
		if (!grid || !grid.parentElement) return;

		var recs = pickRecommendations(diamonds);
		if (recs.length < 3) {
			if (existing) existing.remove();
			return;
		}

		var section = existing || document.createElement('div');
		section.id = 'rb-recs';
		section.className = 'rb-recs';
		section.dataset.rbSignature = signature;
		section.innerHTML = '';

		var title = document.createElement('h2');
		title.className = 'rb-recs-title';
		title.textContent = 'Our Recommendations';
		section.appendChild(title);

		var subtitle = document.createElement('p');
		subtitle.className = 'rb-recs-subtitle';
		subtitle.textContent = 'Hand-picked from the diamonds matching your filters';
		section.appendChild(subtitle);

		var recGrid = document.createElement('div');
		recGrid.className = 'rb-recs-grid';
		section.appendChild(recGrid);

		recs.forEach(function (rec) {
			var a = document.createElement('a');
			a.className = 'rb-rec-card';
			a.href = rec.d.href;

			var badge = document.createElement('div');
			badge.className = 'rb-rec-badge';
			var strong = document.createElement('strong');
			strong.textContent = rec.badge;
			var span = document.createElement('span');
			span.textContent = rec.blurb;
			badge.appendChild(strong);
			badge.appendChild(span);
			a.appendChild(badge);

			if (rec.d.img) {
				var imageWrap = document.createElement('div');
				imageWrap.className = 'rb-rec-image';
				var img = document.createElement('img');
				img.src = rec.d.img;
				img.alt = rec.d.title;
				img.loading = 'lazy';
				imageWrap.appendChild(img);
				a.appendChild(imageWrap);
			}

			var body = document.createElement('div');
			body.className = 'rb-rec-body';

			var name = document.createElement('p');
			name.className = 'rb-rec-name';
			name.textContent = rec.d.title;
			body.appendChild(name);

			var chips = document.createElement('div');
			chips.className = 'rb-rec-chips';
			[
				rec.d.carat ? rec.d.carat + 'ct' : null,
				rec.d.colour ? rec.d.colour + ' Color' : null,
				rec.d.clarity ? rec.d.clarity + ' Clarity' : null,
				rec.d.cut ? rec.d.cut + ' Cut' : null
			].forEach(function (label) {
				if (!label) return;
				var chip = document.createElement('span');
				chip.textContent = label;
				chips.appendChild(chip);
			});
			body.appendChild(chips);

			var price = document.createElement('p');
			price.className = 'rb-rec-price';
			price.textContent = rec.d.priceText;
			body.appendChild(price);

			a.appendChild(body);
			recGrid.appendChild(a);
		});

		if (!existing) grid.parentElement.insertBefore(section, grid);
	}

	/* ---------------------------------------------------------------- */
	/* Amend 2026-09 — fully-assembled reassurance on the final           */
	/* verification step (before "Add to Bag"). We anchor on the app's   */
	/* final add-to-bag/checkout button rather than a stage class, since  */
	/* the review screen exposes no protected class of its own.           */
	/* ---------------------------------------------------------------- */

	var ASSEMBLED_TEXT = 'Your ring arrives fully set — made specifically to hold your selected center diamond or gemstone and professionally set by our jewelers, ready to propose.';

	function applyAssembledMessage(root) {
		if (document.getElementById('rb-assembled-note')) return;

		function makeNote() {
			var note = document.createElement('div');
			note.id = 'rb-assembled-note';
			note.className = 'rb-cart-message';
			note.textContent = ASSEMBLED_TEXT;
			return note;
		}

		/* Preferred anchor: the ring-summary panel on the Complete Ring
		   stage. Its own class is stable; everything inside it is a
		   hashed styled-components class that can change build to
		   build, so we anchor on position (before its 2nd child —
		   i.e. above the line-item list, below the header row)
		   rather than any of those hashes. */
		var summaryRoot = root.querySelector('.diamond-proxy-ring-summary');
		if (summaryRoot && summaryRoot.children.length > 1) {
			summaryRoot.insertBefore(makeNote(), summaryRoot.children[1]);
			return;
		}

		/* Fallback: the "Your ring summary" heading itself, if the
		   panel wrapper above isn't found for some reason */
		var headings = root.querySelectorAll('h1, h2, h3, h4');
		for (var h = 0; h < headings.length; h++) {
			if (/your ring summary/i.test(headings[h].textContent || '')) {
				var head = headings[h];
				head.parentNode.insertBefore(makeNote(), head.nextSibling);
				return;
			}
		}

		/* Fallback: just above the app's final add-to-bag/checkout button */
		var buttons = root.querySelectorAll('button');
		for (var i = 0; i < buttons.length; i++) {
			var btn = buttons[i];
			/* Skip the per-result "Select diamond" card buttons */
			if (btn.classList.contains('diamond-proxy-add-to-cart')) continue;
			if (!/add to (bag|cart)|checkout/i.test(btn.textContent || '')) continue;
			btn.parentNode.insertBefore(makeNote(), btn);
			return;
		}
	}

	/* ---------------------------------------------------------------- */
	/* Boot                                                              */
	/* ---------------------------------------------------------------- */

	function boot() {
		var observer = new MutationObserver(schedule);
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['aria-valuenow', 'src']
		});
		/* Slider drags / typed price values don't always mutate attributes */
		document.addEventListener('input', schedule, true);
		document.addEventListener('pointerup', schedule, true);
		/* Crossing the mobile/desktop breakpoint swaps the collapse defaults */
		if (mobileMQ.addEventListener) mobileMQ.addEventListener('change', schedule);
		else if (mobileMQ.addListener) mobileMQ.addListener(schedule);
		schedule();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();
